import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AccountSwitcher } from "@/components/ui/account-switcher";
import { BrandMark } from "@/components/ui/brand-mark";
import { TopNav } from "@/components/ui/top-nav";
import { type CustomerMembership } from "@/hooks/use-customer-context";
import { accountLink, type DestinationId } from "@/lib/destinations";
import { personInitials } from "@/lib/initials";
import { programLine } from "@/lib/program-line";

interface ShellPerson {
  full_name?: string | null;
  email: string;
}

interface AppHeaderProps {
  memberships: CustomerMembership[];
  active: CustomerMembership;
  profile: ShellPerson;
  /** The page's destination, which the top nav marks; null marks none. */
  destination: DestinationId | null;
}

/**
 * The signed-in header, which scrolls with the page: the utility's mark and
 * name with its program line, the top nav from `lg` marking `destination`, the
 * account switcher for a person with more than one account, and an avatar of
 * the person's initials linking the account page, which the router marks only
 * on the account page itself. Below `lg` the switcher is a row of its own under
 * the avatar, which is also its place in the tab order. From `lg` the brand
 * row holds the mark, the name, the switcher capped at 180px and the avatar
 * drawn last. Below `xl` one row cannot also hold a long utility name and the
 * five nav labels, so the nav is a second row under the brand row, packed to
 * the start; the name grows from nothing into the brand row's spare width, so
 * however long it is it never pushes the switcher or the avatar off the row,
 * and it ellipsizes only past that width. From `xl` everything sits in one
 * 68px row, the nav after the name.
 *
 * When that one row runs out of width the mark and the avatar keep their size
 * and the nav keeps its labels on one line. The switcher gives way first, its
 * label ellipsizing down to its 96px floor; only then does the org name
 * ellipsize, since the name identifies the utility and the chip is secondary.
 */
export function AppHeader({ memberships, active, profile, destination }: AppHeaderProps) {
  const { t } = useTranslation("customer");
  const program = programLine(active);

  return (
    <header className="border-b border-line bg-surface">
      <div className="mx-auto flex max-w-[1140px] flex-wrap items-center gap-x-3 px-4 py-2 lg:gap-x-4 lg:px-9 xl:h-[68px] xl:flex-nowrap xl:py-0">
        <BrandMark orgName={active.org_name} slug={active.org_slug} hasLogo={active.has_logo} />
        <div className="min-w-0 flex-1 lg:mr-1.5 xl:flex-initial">
          <p className="truncate text-sm font-semibold text-ink">{active.org_name}</p>
          {program && <p className="truncate text-2xs text-muted">{program}</p>}
        </div>
        <TopNav active={destination} />
        <Link
          {...accountLink()}
          activeOptions={{ exact: true }}
          aria-label={t("shell.accountLink")}
          className="grid size-11 shrink-0 place-items-center rounded-full lg:order-1"
        >
          <span className="grid size-8 place-items-center rounded-full border border-line-strong bg-canvas text-xs font-semibold text-ink">
            {personInitials(profile.full_name, profile.email)}
          </span>
        </Link>
        {memberships.length > 1 && (
          <div
            data-switcher=""
            className="min-w-0 basis-full pt-2 lg:min-w-24 lg:max-w-45 lg:shrink-[100] lg:basis-auto lg:pt-0"
          >
            <AccountSwitcher memberships={memberships} active={active} />
          </div>
        )}
      </div>
    </header>
  );
}
